<template>
  <div>
    <div class="d-flex flex-wrap align-center mb-4" style="gap: 12px">
      <v-select
        :value="period"
        :items="periods"
        label="Период движений"
        outlined dense hide-details
        style="max-width: 240px"
        @change="$emit('update:period', $event)"
      />
      <template v-if="period === 'custom'">
        <v-text-field
          :value="customRange.from" type="date" label="С" outlined dense hide-details style="max-width: 170px"
          @input="setCustom('from', $event)"
        />
        <v-text-field
          :value="customRange.to" type="date" label="По" outlined dense hide-details style="max-width: 170px"
          @input="setCustom('to', $event)"
        />
      </template>
      <span class="caption grey--text text--darken-1">{{ periodHint }}</span>
    </div>

    <StateBlock v-if="loading && !wallet" state="loading" />
    <StateBlock v-else-if="error && error.status === 403" state="forbidden" />
    <StateBlock v-else-if="error" state="error" @retry="$emit('reload')" />
    <StateBlock v-else-if="!wallet" state="empty" text="Сервер не вернул сводку по остаткам." />

    <template v-else>
      <WalletSummary :wallet="wallet" :spent-label="spentLabel" class="mb-4" />

      <v-alert v-if="isAdmin && wallet.needsReconciliation" type="warning" outlined dense class="mb-4">
        <div class="d-flex align-center">
          <span>Есть данные, требующие сверки</span>
          <v-btn x-small text class="ml-2" @click="showIssues = !showIssues">{{ showIssues ? 'Скрыть' : 'Подробнее' }}</v-btn>
        </div>
        <ul v-if="showIssues" class="mt-2 caption">
          <li v-for="issue in issues" :key="issue.key">{{ issue.label }}: {{ issue.count }}</li>
          <li v-if="wallet.unmapped.hasData">Есть операции, для которых не определена услуга (блок внизу)</li>
        </ul>
      </v-alert>

      <div class="d-flex flex-wrap align-center mb-2" style="gap: 12px">
        <v-btn-toggle v-model="stockFilter" mandatory dense color="primary">
          <v-btn small value="all">Все</v-btn>
          <v-btn small value="in_stock">С остатком</v-btn>
          <v-btn small value="out">Закончились</v-btn>
        </v-btn-toggle>
        <v-text-field v-model="search" label="Поиск теста" append-icon="mdi-magnify" outlined dense hide-details clearable style="max-width: 260px" />
      </div>

      <v-data-table
        :headers="headers"
        :items="rows"
        item-key="id"
        show-expand
        single-expand
        disable-pagination
        hide-default-footer
        disable-sort
        :loading="loading"
        no-data-text="Тестов не найдено"
        class="wallet-table"
      >
        <template v-slot:item.name="{ item }">
          <b>{{ item.name }}</b>
          <span v-if="item.total.balance <= 0" class="ended-tag">Закончилась</span>
        </template>
        <template v-slot:item.total="{ item }">
          <b :class="{ 'error--text': item.total.balance < 0 }">{{ item.total.balance }}</b>
        </template>
        <template v-slot:item.buffer="{ item }">{{ item.buffer.balance }}</template>
        <template v-slot:item.received="{ item }">{{ item.total.received }}</template>
        <template v-slot:item.spent="{ item }">{{ item.total.spent }}</template>
        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-end">
            <v-tooltip v-if="canAccrue" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" @click="openTransfer('accrue', item.code)"><v-icon small>mdi-plus</v-icon></v-btn>
              </template>
              <span>Начислить</span>
            </v-tooltip>
            <v-tooltip v-else-if="canBuy" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" :loading="buyingCode === item.code" @click="buyTest(item)"><v-icon small>mdi-cart-plus</v-icon></v-btn>
              </template>
              <span>Купить</span>
            </v-tooltip>
            <v-tooltip v-if="canDistribute" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" :disabled="item.buffer.balance <= 0" @click="openTransfer('distribute', item.code)">
                  <v-icon small>mdi-swap-horizontal</v-icon>
                </v-btn>
              </template>
              <span>Распределить</span>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:expanded-item="{ headers: cols, item }">
          <td :colspan="cols.length" class="py-3">
            <table class="wallet-breakdown">
              <thead>
                <tr><th>{{ item.name }}</th><th>Остаток</th><th>Получено</th><th>Израсходовано</th></tr>
              </thead>
              <tbody>
                <tr class="font-weight-medium"><td>Всего по тесту</td><td>{{ item.total.balance }}</td><td>{{ item.total.received }}</td><td>{{ item.total.spent }}</td></tr>
                <tr><td>Буфер компании</td><td>{{ item.buffer.balance }}</td><td>{{ item.buffer.received }}</td><td>{{ item.buffer.spent }}</td></tr>
                <tr v-for="m in item.managers" :key="m.id"><td>{{ m.name }}</td><td>{{ m.balance }}</td><td>{{ m.received }}</td><td>{{ m.spent }}</td></tr>
              </tbody>
            </table>
            <div class="caption grey--text text--darken-1 mt-2">
              Переводы менеджерам видны в строках буфера и менеджера. «Всего» учитывает только поступления в компанию и расход на кандидатов.
            </div>
          </td>
        </template>
      </v-data-table>

      <div class="caption grey--text text--darken-1 mt-2">
        * за выбранный период<template v-if="canAccrue || canBuy"> · ＋ — {{ canAccrue ? 'начислить в буфер' : 'купить в каталоге' }}</template><template v-if="canDistribute"> · ⇄ — распределить из буфера менеджеру</template>
        · консалтинг и обучение не хранятся в остатках, они видны в заказах
      </div>

      <div v-if="isAdmin && wallet.unmapped.hasData" class="mt-6">
        <h3 class="font-md mb-1">Не определена услуга</h3>
        <div class="caption grey--text text--darken-1 mb-2">
          Старые операции, для которых сервер не смог определить тест. В таблицу и плашки они не входят.
        </div>
        <v-simple-table dense>
          <thead>
            <tr><th>Код</th><th>Получено</th><th>Израсходовано</th></tr>
          </thead>
          <tbody>
            <tr v-for="(m, ind) in wallet.unmapped.movements" :key="ind">
              <td>{{ m.code || '—' }}</td><td>{{ m.total.received }}</td><td>{{ m.total.spent }}</td>
            </tr>
            <tr v-if="!wallet.unmapped.movements.length">
              <td>Итого</td><td>{{ wallet.unmapped.total.received }}</td><td>{{ wallet.unmapped.total.spent }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>
    </template>

    <WalletTransferDialog
      v-model="transfer.show"
      :mode="transfer.mode"
      :corp-id="corpId"
      :items="wallet ? wallet.items : []"
      :managers="wallet ? wallet.managers : []"
      :preset-test="transfer.test"
      @success="$emit('reload')"
    />

    <v-dialog v-model="exportDialog.show" max-width="460" :persistent="exportDialog.loading">
      <v-card>
        <v-card-title class="font-md">Выгрузка остатков в Excel</v-card-title>
        <v-card-text>
          <div class="d-flex" style="gap: 12px">
            <v-text-field v-model="exportDialog.from" type="date" label="С" outlined dense hide-details />
            <v-text-field v-model="exportDialog.to" type="date" label="По" outlined dense hide-details />
          </div>
          <div class="caption grey--text text--darken-1 mt-3">
            Даты задают период движений (получено и израсходовано). Остаток в выгрузке показан на текущий момент:
            остаток на прошлые даты сервер не хранит.
          </div>
          <v-alert v-if="exportDialog.error" type="error" dense outlined class="mt-3 mb-0">{{ exportDialog.error }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="exportDialog.loading" @click="exportDialog.show = false">Отмена</v-btn>
          <v-btn color="primary" :loading="exportDialog.loading" :disabled="!!exportRangeError" @click="doExport">Выгрузить</v-btn>
        </v-card-actions>
        <div v-if="exportRangeError" class="caption error--text px-6 pb-3">{{ exportRangeError }}</div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment';
import { debounce } from 'lodash';
import api from '@/common/api';
import StateBlock from '@/components/ui/StateBlock.vue';
import { CORPCLIENT } from '@/common/constants/userTypes';
import exportWalletBalances from '@/common/exportExcel/WalletBalancesToTable';
import {
  DATA_QUALITY_FLAGS, DATA_QUALITY_LABELS, findWalletStatistics, parseWalletStatistics,
} from '@/common/models/walletStatistics';
import WalletSummary from './WalletSummary.vue';
import WalletTransferDialog from './WalletTransferDialog.vue';
import { BALANCE_PERIODS, PERIOD_HINT } from './walletPeriods';

const isKnownItem = (i) => i.total.balance || i.total.received || i.total.spent
  || i.buffer.balance || i.buffer.received || i.buffer.spent
  || i.managers.some((m) => m.balance || m.received || m.spent);

// Если менеджеров больше — вместо колонок одна «У менеджеров», разбивка в раскрытии строки (Э-5).
const MAX_MANAGER_COLUMNS = 4;

export default {
  name: 'ClientBalances',
  components: { StateBlock, WalletSummary, WalletTransferDialog },
  props: {
    corpId: { type: [Number, String], required: true },
    company: { type: String, default: '' },
    wallet: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    error: { type: Object, default: null },
    period: { type: String, required: true },
    customRange: { type: Object, default: () => ({}) },
    walletPeriod: { type: Object, default: () => ({}) },
    isAdmin: { type: Boolean, default: false },
    canAccrue: { type: Boolean, default: false },
    canDistribute: { type: Boolean, default: false },
    canBuy: { type: Boolean, default: false },
  },
  data: () => ({
    periods: BALANCE_PERIODS,
    periodHint: PERIOD_HINT,
    stockFilter: 'all',
    search: '',
    showIssues: false,
    transfer: { show: false, mode: 'accrue', test: null },
    buyingCode: null,
    exportDialog: {
      show: false, from: '', to: '', loading: false, error: '',
    },
  }),
  created() {
    this.applyCustom = debounce(this.flushCustom, 600);
  },
  beforeDestroy() {
    this.applyCustom.cancel();
  },
  computed: {
    spentLabel() {
      const period = BALANCE_PERIODS.find((p) => p.value === this.period);
      return `Израсходовано: ${period ? period.text.toLowerCase() : 'за период'}`;
    },
    // Тесты, которые у компании были: с остатком или с движениями. Полный список — в каталоге.
    knownItems() {
      return this.wallet ? this.wallet.items.filter(isKnownItem) : [];
    },
    rows() {
      return this.knownItems.filter(this.matchesFilters).map((i) => {
        // Колонки менеджеров и «У менеджеров» — готовыми полями строки.
        const row = { ...i, managersSum: i.managers.reduce((s, m) => s + m.balance, 0) };
        this.managerColumns.forEach((m) => { row[`m_${m.id}`] = this.managerBalance(i, m.id); });
        return row;
      });
    },
    exportRangeError() {
      const { from, to } = this.exportDialog;
      if (!from || !to) return 'Укажите обе даты';
      if (from > to) return 'Дата «С» позже даты «По»';
      return '';
    },
    managerColumns() {
      const managers = this.wallet ? this.wallet.managers : [];
      return managers.length <= MAX_MANAGER_COLUMNS ? managers : [];
    },
    headers() {
      const managersCols = this.managerColumns.length
        ? this.managerColumns.map((m) => ({ text: m.name, value: `m_${m.id}`, align: 'end' }))
        : [{ text: `У менеджеров${this.wallet && this.wallet.managers.length ? `: ${this.wallet.managers.length}` : ''}`, value: 'managersSum', align: 'end' }];
      return [
        { text: 'Тест', value: 'name' },
        { text: 'Итого', value: 'total', align: 'end' },
        { text: 'Буфер', value: 'buffer', align: 'end' },
        ...managersCols,
        { text: 'Получено*', value: 'received', align: 'end' },
        { text: 'Израсходовано*', value: 'spent', align: 'end' },
        {
 text: '', value: 'actions', align: 'end', width: '100px'
},
        { text: '', value: 'data-table-expand' },
      ];
    },
    issues() {
      if (!this.wallet) return [];
      return DATA_QUALITY_FLAGS
        .filter((key) => this.wallet.dataQuality.flags[key] > 0)
        .map((key) => ({ key, label: DATA_QUALITY_LABELS[key], count: this.wallet.dataQuality.flags[key] }));
    },
  },
  methods: {
    managerBalance(item, managerId) {
      const m = item.managers.find((x) => x.id === managerId);
      return m ? m.balance : 0;
    },
    openTransfer(mode, test = null) {
      this.transfer = { show: true, mode, test };
    },
    // «＋ Купить»: кладём услугу в корзину прямо отсюда; если карточка каталога не связана с тестом — открываем каталог.
    async buyTest(item) {
      this.buyingCode = item.code;
      if (!(this.$store.getters['services/services/GET_ALL_SERVICES'] || []).length) {
        await this.$store.dispatch('services/services/GET_ALL_SERVICES');
      }
      const card = (this.$store.getters['services/services/GET_ALL_SERVICES'] || [])
        .find((c) => c.status === 'published' && c.is_visible !== false && String(c.nomenclature) === String(item.id));
      if (!card) {
        this.buyingCode = null;
        this.$router.push({ name: 'list-services' });
        return;
      }
      if (this.$store.getters['basket/basket/itemInBasket'](card.id)) {
        this.buyingCode = null;
        this.$toast.info('Уже в корзине', '', { position: 'topRight' });
        return;
      }
      try {
        await this.$store.dispatch('basket/basket/addToBasket', { service_card_id: card.id, quantity: 1 });
        await this.$store.dispatch('basket/basket/fetchUserBasket');
        this.$toast.success('Добавлено в корзину', '', { position: 'topRight' });
      } catch (e) {
        this.$toast.error('Не удалось добавить в корзину', '', { position: 'topRight' });
      } finally {
        this.buyingCode = null;
      }
    },
    // Свой диапазон: поле даты шлёт значение по мере набора, поэтому ждём паузу
    setCustom(key, value) {
      this.pendingCustom = { ...(this.pendingCustom || this.customRange), [key]: value };
      this.applyCustom();
    },
    // Применяем, только если даты заданы целиком (год от 2000), обе и «С» не позже «По»
    flushCustom() {
      const next = this.pendingCustom;
      this.pendingCustom = null;
      if (!next || !next.from || !next.to || next.from < '2000-01-01' || next.to < '2000-01-01') return;
      if (next.from > next.to) {
        this.$toast.warning('Дата «С» позже даты «По»', '', { position: 'topRight' });
        return;
      }
      this.$emit('update:custom-range', next);
    },
    matchesFilters(i) {
      const q = (this.search || '').trim().toLowerCase();
      if (this.stockFilter === 'in_stock' && i.total.balance <= 0) return false;
      if (this.stockFilter === 'out' && i.total.balance > 0) return false;
      return !q || i.name.toLowerCase().includes(q) || i.code.toLowerCase().includes(q);
    },
    // Выгрузка спрашивает период: по умолчанию тот, что выбран на странице
    exportXlsx() {
      const { from, to } = this.walletPeriod || {};
      this.exportDialog = {
        show: true,
        from: from || moment().subtract(29, 'days').format('YYYY-MM-DD'),
        to: to || moment().format('YYYY-MM-DD'),
        loading: false,
        error: '',
      };
    },
    async doExport() {
      const { from, to } = this.exportDialog;
      this.exportDialog.loading = true;
      this.exportDialog.error = '';
      try {
        const params = { from_date: from, to_date: to };
        const user = this.$store.getters['auth/user'];
        // Как и в сторе: корп берёт сводку из get-me, а если её там нет — из карточки корпа
        let raw = user && user.user_type === CORPCLIENT
          ? findWalletStatistics(await api.get('/users/get-me', { params }))
          : null;
        if (!raw) raw = (await api.get(`/users/corp_clients/${this.corpId}/`, { params })).wallet_statistics;
        const wallet = parseWalletStatistics(raw);
        if (!wallet) throw new Error('empty');
        // В файл идёт то же, что на экране: с учётом поиска и фильтра остатка
        const items = wallet.items.filter(isKnownItem).filter(this.matchesFilters);
        exportWalletBalances({ items, company: this.company, period: { from, to } });
        this.exportDialog.show = false;
      } catch (e) {
        this.exportDialog.error = 'Не удалось получить данные за период. Попробуйте ещё раз.';
      } finally {
        this.exportDialog.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.ended-tag {
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  background: #fff8e1;
  color: #f57f17;
}

.wallet-breakdown {
  border-collapse: collapse;
  min-width: 420px;

  th, td {
    padding: 4px 16px 4px 0;
    text-align: right;
    font-size: 13px;
  }

  th:first-child, td:first-child {
    text-align: left;
  }

  th {
    color: #757575;
    font-weight: 500;
  }
}
</style>
